'use client';

import { css } from '@bts-web/utils-style-engine';
import { Locale, useClientTranslation } from '@bts-web/utils-lokalise';
import { type TimeScalar } from '@bts-web/utils-formatting';
import {
  convertToLocaleDateString,
  timeScalarToDate,
} from '@bts-web/utils-formatting-client';
import type { OrderFailReason, OrderStatus } from '@bts-web/data-layer/server';
import { orderFailReasonTranslationKeys } from './orderFailReasonTranslationKeys';
import { InstantClockDisplayedText } from '../../../common/components';

const rowDetailsStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 'body.medium_medium',
  lineHeight: 'body.medium_medium',
  letterSpacing: 'body.medium_medium',
  fontWeight: 'body.medium',

  '& > :first-child': {
    color: 'neutrals.text_secondary',
  },
  '& > :last-child': {
    color: 'neutrals.text_primary',
    textAlign: 'end',
  },
});

const sectionStyles = css({
  backgroundColor: 'neutrals.card_fill_primary',
  borderRadius: '24px',
  padding: 'medium',
  marginX: 'medium',
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
});

export const OrderDetailsMainTimeRows = ({
  locale,
  orderId,
  status,
  createdAt,
  expiresAt,
  translations,
}: {
  locale: Locale;
  orderId: string;
  status: OrderStatus;
  createdAt?: TimeScalar | null;
  expiresAt?: TimeScalar | null;
  translations: { orderCreated: string };
}) => {
  const { t } = useClientTranslation();

  const createdAtDate = createdAt ? timeScalarToDate(createdAt) : null;

  if (!createdAtDate) {
    console.error('[OrderDetails] Missing createdAt for order details row', {
      orderId,
      status,
    });
  }

  const createdAtText = createdAtDate
    ? t('tx_details_date_hour', {
        date: createdAtDate.toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }),
        time: createdAtDate.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
        }),
      })
    : null;

  return (
    <>
      {createdAtText && (
        <div className={rowDetailsStyles}>
          <span>{translations.orderCreated}</span>
          <InstantClockDisplayedText>{createdAtText}</InstantClockDisplayedText>
        </div>
      )}
      {expiresAt && (
        <div className={rowDetailsStyles}>
          <span>{t('stop_order_valid_until', { date: '' })}</span>
          <span>
            {convertToLocaleDateString(expiresAt, locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
        </div>
      )}
    </>
  );
};

export const OrderDetailsExecutedTimeSection = ({
  locale,
  orderId,
  status,
  updatedAt,
  translations,
}: {
  locale: Locale;
  orderId: string;
  status: OrderStatus;
  updatedAt?: TimeScalar | null;
  translations: { orderExecuted: string };
}) => {
  const { t } = useClientTranslation();

  if (status !== 'FINISHED') {
    return null;
  }

  const executedAtDate = updatedAt ? timeScalarToDate(updatedAt) : null;

  if (!executedAtDate) {
    console.error('[OrderDetails] Missing updatedAt for executed order row', {
      orderId,
      status,
    });

    return null;
  }

  const executedAtText = t('tx_details_date_hour', {
    date: convertToLocaleDateString(updatedAt, locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
    time: executedAtDate.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  });

  return (
    <div className={sectionStyles}>
      <div className={rowDetailsStyles}>
        <span>{translations.orderExecuted}</span>
        <InstantClockDisplayedText>{executedAtText}</InstantClockDisplayedText>
      </div>
    </div>
  );
};

export const OrderDetailsCancelledTimeSection = ({
  locale,
  updatedAt,
  failReason,
  assetSymbol,
  translations,
}: {
  locale: Locale;
  updatedAt?: TimeScalar | null;
  failReason?: OrderFailReason | null;
  assetSymbol?: string | null;
  translations: { orderCancelled: string; reason: string };
}) => {
  const { t } = useClientTranslation();

  const cancelledAtContent =
    updatedAt &&
    t('tx_details_date_hour', {
      date: convertToLocaleDateString(updatedAt, locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
      time: timeScalarToDate(updatedAt).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    });

  if (!cancelledAtContent && !failReason) {
    return null;
  }

  return (
    <div className={sectionStyles}>
      {cancelledAtContent && (
        <div className={rowDetailsStyles}>
          <span>{translations.orderCancelled}</span>
          <InstantClockDisplayedText>
            {cancelledAtContent}
          </InstantClockDisplayedText>
        </div>
      )}
      {failReason && (
        <div className={rowDetailsStyles}>
          <span>{translations.reason}</span>
          <span>
            {t(orderFailReasonTranslationKeys[failReason], {
              ticker: assetSymbol,
            })}
          </span>
        </div>
      )}
    </div>
  );
};
